import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, MATERIALS, MATERIAL_COLORS, MATERIAL_SLUGS, formatApiError } from "@/lib/apiClient";
import { ArrowLeft, Save, Plus, Trash2, Camera, ChevronDown, Search, Check } from "lucide-react";
import VoiceDictate from "@/components/VoiceDictate";
import PhotoCapture from "@/components/PhotoCapture";

export default function VisitForm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const presetClient = params.get("client_id") || "";

  const [clients, setClients] = useState([]);
  const [clientOpen, setClientOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const clientWrapRef = useRef(null);
  const [form, setForm] = useState({
    client_id: presetClient,
    visit_date: new Date().toISOString().slice(0, 10),
    materials: [],
    outcome: "a_relancer",
    next_visit_date: "",
    notes: "",
    photos: [],
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/clients");
      setClients(data);
    })();
  }, []);

  // Close client dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (clientWrapRef.current && !clientWrapRef.current.contains(e.target)) {
        setClientOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedClient = clients.find((c) => c.id === form.client_id);
  const filteredClients = clientSearch.trim()
    ? clients.filter((c) => {
        const q = clientSearch.trim().toLowerCase();
        return (
          c.company_name?.toLowerCase().includes(q) ||
          c.city?.toLowerCase().includes(q) ||
          c.contact_name?.toLowerCase().includes(q)
        );
      })
    : clients;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleMaterial = (type) => {
    setForm((f) => {
      const exists = f.materials.find((m) => m.type === type);
      if (exists) {
        return { ...f, materials: f.materials.filter((m) => m.type !== type) };
      }
      return { ...f, materials: [...f.materials, { type, tonnage: 0, price_per_ton: 0 }] };
    });
  };

  const updateMaterial = (type, field, value) => {
    setForm((f) => ({
      ...f,
      materials: f.materials.map((m) => (m.type === type ? { ...m, [field]: parseFloat(value) || 0 } : m)),
    }));
  };

  const total = form.materials.reduce((s, m) => s + (m.tonnage || 0) * (m.price_per_ton || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.client_id) {
      setError("Sélectionne un client.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        client_id: form.client_id,
        visit_date: new Date(form.visit_date).toISOString(),
        materials: form.materials,
        outcome: form.outcome,
        next_visit_date: form.next_visit_date ? new Date(form.next_visit_date).toISOString() : null,
        notes: form.notes,
        photos: form.photos,
      };
      await api.post("/visits", payload);
      navigate(`/clients/${form.client_id}`);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <h1 className="font-heading font-black text-3xl tracking-tight">Nouvelle visite</h1>

      <form onSubmit={submit} className="bg-white rounded-3xl border border-stone-100 p-5 sm:p-6 space-y-5 shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
        {/* Client select */}
        <div>
          <label className="block text-sm font-bold text-stone-700 mb-2">Client *</label>
          {clients.length === 0 ? (
            <div className="text-sm bg-amber-50 text-amber-700 border border-amber-200 rounded-2xl px-4 py-3">
              Aucun client.{" "}
              <Link to="/clients/new" className="font-bold underline">{"Crées-en un d'abord"}</Link>.
            </div>
          ) : (
            <div className="relative" ref={clientWrapRef}>
              <button
                type="button"
                data-testid="visit-client-trigger"
                onClick={() => {
                  setClientOpen((o) => !o);
                  setClientSearch("");
                }}
                className={`w-full flex items-center justify-between gap-2 bg-stone-50 border ${
                  clientOpen ? "bg-white border-emerald-400 ring-4 ring-emerald-100" : "border-stone-200"
                } hover:bg-white rounded-2xl px-4 py-3 transition-all text-left font-medium`}
              >
                <span className={selectedClient ? "text-stone-900" : "text-stone-400"}>
                  {selectedClient
                    ? `${selectedClient.company_name}${selectedClient.city ? ` (${selectedClient.city})` : ""}`
                    : "— Choisir un client —"}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-stone-400 shrink-0 transition-transform ${clientOpen ? "rotate-180" : ""}`}
                  strokeWidth={2.5}
                />
              </button>

              {clientOpen && (
                <div className="absolute z-20 mt-2 w-full bg-white border border-stone-200 rounded-2xl shadow-2xl overflow-hidden">
                  <div className="p-2 border-b border-stone-100 bg-stone-50">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" strokeWidth={2.5} />
                      <input
                        type="text"
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        placeholder="Rechercher un client..."
                        autoFocus
                        data-testid="visit-client-search"
                        className="w-full bg-white border border-stone-200 rounded-xl pl-9 pr-3 py-2 text-sm font-medium focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none"
                      />
                    </div>
                  </div>
                  <ul
                    data-testid="visit-client-list"
                    className="max-h-64 overflow-y-auto py-1 client-scroll"
                  >
                    {filteredClients.length === 0 ? (
                      <li className="px-4 py-3 text-sm text-stone-400 italic">Aucun client trouvé</li>
                    ) : (
                      filteredClients.map((c) => {
                        const isSel = c.id === form.client_id;
                        return (
                          <li key={c.id}>
                            <button
                              type="button"
                              data-testid={`visit-client-option-${c.id}`}
                              onClick={() => {
                                update("client_id", c.id);
                                setClientOpen(false);
                                setClientSearch("");
                              }}
                              className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between gap-2 transition-colors ${
                                isSel ? "bg-emerald-50 text-emerald-800 font-bold" : "hover:bg-stone-50 text-stone-700 font-medium"
                              }`}
                            >
                              <span className="truncate">
                                {c.company_name}
                                {c.city && <span className="text-stone-400 font-medium"> · {c.city}</span>}
                              </span>
                              {isSel && <Check className="w-4 h-4 text-emerald-600 shrink-0" strokeWidth={3} />}
                            </button>
                          </li>
                        );
                      })
                    )}
                  </ul>
                  <div className="px-3 py-2 border-t border-stone-100 bg-stone-50 text-[11px] text-stone-500 font-medium flex items-center justify-between">
                    <span>{filteredClients.length} / {clients.length} client{clients.length > 1 ? "s" : ""}</span>
                    <Link to="/clients/new" className="text-emerald-600 hover:text-emerald-700 font-bold">+ Nouveau</Link>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Date */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold text-stone-700 mb-2">Date de visite</label>
            <input
              data-testid="visit-date"
              type="date"
              value={form.visit_date}
              onChange={(e) => update("visit_date", e.target.value)}
              className="w-full bg-stone-50 border border-stone-200 focus:bg-white focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 outline-none rounded-2xl px-4 py-3 transition-all font-medium"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-stone-700 mb-2">Prochaine relance</label>
            <input
              data-testid="visit-next-date"
              type="date"
              value={form.next_visit_date}
              onChange={(e) => update("next_visit_date", e.target.value)}
              className="w-full bg-stone-50 border border-stone-200 focus:bg-white focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 outline-none rounded-2xl px-4 py-3 transition-all font-medium"
            />
          </div>
        </div>

        {/* Materials */}
        <div>
          <label className="block text-sm font-bold text-stone-700 mb-2">Matières discutées</label>
          <div className="flex flex-wrap gap-2 mb-3">
            {MATERIALS.map((m) => {
              const active = form.materials.some((x) => x.type === m);
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => toggleMaterial(m)}
                  data-testid={`material-toggle-${MATERIAL_SLUGS[m]}`}
                  className={`px-4 py-2 rounded-full text-sm font-bold transition-all active:scale-95 ${
                    active ? `${MATERIAL_COLORS[m]} shadow-lg` : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                  }`}
                >
                  {active ? "✓ " : "+ "}
                  {m}
                </button>
              );
            })}
          </div>
          {form.materials.length > 0 && (
            <div className="space-y-2">
              {form.materials.map((m) => (
                <div key={m.type} className="bg-stone-50 rounded-2xl p-3 flex flex-wrap items-center gap-2">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${MATERIAL_COLORS[m.type]}`}>{m.type}</span>
                  <input
                    data-testid={`material-tonnage-${MATERIAL_SLUGS[m.type]}`}
                    type="number"
                    step="0.1"
                    min="0"
                    value={m.tonnage}
                    onChange={(e) => updateMaterial(m.type, "tonnage", e.target.value)}
                    placeholder="Tonnes"
                    className="w-24 bg-white border border-stone-200 rounded-xl px-3 py-2 text-sm font-medium focus:border-emerald-400 outline-none"
                  />
                  <span className="text-stone-400 text-sm">t ·</span>
                  <input
                    data-testid={`material-price-${MATERIAL_SLUGS[m.type]}`}
                    type="number"
                    step="0.01"
                    min="0"
                    value={m.price_per_ton}
                    onChange={(e) => updateMaterial(m.type, "price_per_ton", e.target.value)}
                    placeholder="€ / t"
                    className="w-24 bg-white border border-stone-200 rounded-xl px-3 py-2 text-sm font-medium focus:border-emerald-400 outline-none"
                  />
                  <span className="text-stone-400 text-sm">€/t</span>
                  <span className="ml-auto font-bold text-sm tabular-nums text-emerald-700">
                    {((m.tonnage || 0) * (m.price_per_ton || 0)).toLocaleString("fr-FR")} €
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleMaterial(m.type)}
                    className="text-stone-400 hover:text-rose-600"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 border-t border-stone-100">
                <span className="font-bold text-stone-600 text-sm">Total estimé</span>
                <span className="font-heading font-black text-xl text-emerald-600" data-testid="visit-total">
                  {total.toLocaleString("fr-FR")} €
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Outcome */}
        <div>
          <label className="block text-sm font-bold text-stone-700 mb-2">Résultat de la visite</label>
          <div className="flex flex-wrap gap-2">
            {[
              ["interesse", "Intéressé"],
              ["a_relancer", "À relancer"],
              ["vente", "Vente conclue"],
              ["pas_interesse", "Pas intéressé"],
            ].map(([k, label]) => (
              <button
                key={k}
                type="button"
                data-testid={`outcome-${k}`}
                onClick={() => update("outcome", k)}
                className={`px-4 py-2 rounded-full text-sm font-bold border transition-all active:scale-95 ${
                  form.outcome === k
                    ? "bg-stone-900 text-white border-stone-900"
                    : "bg-white border-stone-200 text-stone-700 hover:bg-stone-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-bold text-stone-700 mb-2">Compte-rendu</label>
          <div className="relative">
            <textarea
              data-testid="visit-notes"
              rows={4}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Ce qui a été dit, prochaines étapes, contraintes du client..."
              className="w-full bg-stone-50 border border-stone-200 focus:bg-white focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 outline-none rounded-2xl px-4 py-3 pr-14 transition-all"
            />
            <div className="absolute bottom-3 right-3">
              <VoiceDictate
                compact
                onTranscript={(text) =>
                  update("notes", form.notes ? form.notes + " " + text : text)
                }
              />
            </div>
          </div>
        </div>

        {/* Photos */}
        <div>
          <label className="block text-sm font-bold text-stone-700 mb-2 inline-flex items-center gap-2">
            <Camera className="w-4 h-4" strokeWidth={2.5} /> Photos du site
          </label>
          <PhotoCapture
            photos={form.photos}
            onChange={(p) => update("photos", p)}
          />
        </div>

        {error && (
          <div data-testid="visit-error" className="text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-2xl px-4 py-3">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy || clients.length === 0}
          data-testid="visit-submit"
          className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3.5 rounded-full transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/30 active:scale-[0.98]"
        >
          <Save className="w-4 h-4" strokeWidth={3} /> {busy ? "Enregistrement..." : "Enregistrer la visite"}
        </button>
      </form>
    </div>
  );
}
